import React, { forwardRef, useEffect } from 'react'
import { Container, Dialog, Grid, IconButton, Slide } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { makeStyles } from '@material-ui/styles'
import { connect } from 'react-redux'

import { isEmpty } from 'lodash'
import { addDays } from 'date-fns'
import { loadTeamMembersStart } from '../../../redux/modules/teamMembers'
import {
  loadProgramMembersStart,
  loadProgramStatsStart,
  resetProgramDetailsState,
  resetMembersRemoveState,
  setPage,
  setSearch,
} from '../../../redux/modules/teamProgramDetails'
import { resetState, setMissingMembersStart } from '../../../redux/modules/teamAddProgramMembers'
import { selectTeam } from '../../../redux/selectors/teamSelectors'
import { selectMissingMembers } from '../../../redux/selectors/teamAddProgramMembersSelectors'
import { updateProgramStart } from '../../../redux/modules/teamPrograms'
import CardProgramSummary from './CardProgramSummary'
import CardProgramStats from './CardProgramStats'
import CardProgramMembers from './CardProgramMembers'
import ChipProgramStatus from './ChipProgramStatus'
import { getProgramStatus } from './utils'
import DialogAddProgramMembers from './DialogAddProgramMembers'
import EditableProgramName from './EditableProgramName'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.grey[100],
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  container: {
    paddingTop: theme.spacing(5),
    maxWidth: '860px',
  },
  chipContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
}))

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const DialogProgramDetails = ({
  team,
  program,
  programMembers,
  missingMembers,
  programStats,
  isLoadingProgramStats,
  isUpdatingProgram,
  errorSettingMissingMembers,
  loadTeamMembersStart,
  loadProgramStatsStart,
  loadProgramMembersStart,
  setPage,
  setSearch,
  setMissingMembersStart,
  resetState: resetAddMembersState,
  resetProgramDetailsState,
  resetMembersRemoveState,
  updateProgramStart,
  onClose,
}) => {
  const classes = useStyles()

  const handleClickAddMembers = () => {
    setMissingMembersStart()
  }

  const handleCloseAddMembers = () => {
    resetAddMembersState()
  }

  const handleUpdateProgram = ({ name, endDate }) => {
    // WARNING This is a TEMPORARY fix as the end date is exclusive in the backend
    // We need to add a day here just so the program is available on the selected day
    // Note: We are subtracting a day when displaying it to the user
    const endDateInclusive = endDate ? addDays(endDate, 1) : undefined
    updateProgramStart(program.programId, { name, endDate: endDateInclusive })
  }

  useEffect(() => {
    if (team && program.programId) {
      loadTeamMembersStart(team.id)
      loadProgramMembersStart(program.programId)
      loadProgramStatsStart(program.programId)
    }
  }, [
    team,
    program.programId,
    loadProgramMembersStart,
    loadProgramStatsStart,
    loadTeamMembersStart,
  ])

  // Clean up the state on unmount
  useEffect(() => {
    return () => {
      resetAddMembersState()
      resetProgramDetailsState()
      resetMembersRemoveState()
    }
  }, [resetAddMembersState, resetMembersRemoveState, resetProgramDetailsState])

  const status = getProgramStatus(program)
  return (
    <>
      <Dialog open fullScreen classes={{ paper: classes.root }} TransitionComponent={Transition}>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <Close />
        </IconButton>
        <Container className={classes.container}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={8}>
              <EditableProgramName
                programName={program.name}
                submitting={isUpdatingProgram}
                onSubmit={handleUpdateProgram}
              />
            </Grid>
            <Grid item xs={12} sm={4} className={classes.chipContainer}>
              <ChipProgramStatus status={status} />
            </Grid>
            <Grid item xs={12} md={8}>
              <CardProgramSummary
                program={program}
                isUpdatingProgram={isUpdatingProgram}
                onUpdateProgram={handleUpdateProgram}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CardProgramStats programStats={programStats} loading={isLoadingProgramStats} />
            </Grid>
            <Grid item xs={12}>
              <CardProgramMembers
                programMembers={programMembers}
                errorSettingMissingMembers={errorSettingMissingMembers}
                setPage={setPage}
                setSearch={setSearch}
                onClickAddMembers={handleClickAddMembers}
              />
            </Grid>
          </Grid>
        </Container>
      </Dialog>
      {!isEmpty(missingMembers) && <DialogAddProgramMembers onClose={handleCloseAddMembers} />}
    </>
  )
}

const mapStateToProps = state => {
  const team = selectTeam(state)
  const missingMembers = selectMissingMembers(state)
  const { programMembers, programStats, isLoadingProgramStats } = state.teamProgramDetails
  const { isUpdatingProgram } = state.teamPrograms
  const { errorSettingMissingMembers } = state.teamAddProgramMembers

  return {
    team,
    programMembers,
    missingMembers,
    programStats,
    isLoadingProgramStats,
    isUpdatingProgram,
    errorSettingMissingMembers,
  }
}

const mapDispatchToProps = {
  loadTeamMembersStart,
  loadProgramMembersStart,
  loadProgramStatsStart,
  setPage,
  setSearch,
  setMissingMembersStart,
  resetState,
  resetProgramDetailsState,
  resetMembersRemoveState,
  updateProgramStart,
}

export default connect(mapStateToProps, mapDispatchToProps)(DialogProgramDetails)
